import { useSelector } from 'react-redux';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import { numberObjectKeys } from '~/_shared/utils/object/objectEnumerators';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type AppState } from '~/store/app.store';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { boundaryToolActiveBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import {
  boundaryFiltersStateSelector, boundaryLocationsFiltersStateSelector,
} from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.stateSelectors';
import { mapSettingsBoundariesIsolatedBoundariesSelector } from '../../boundaries/mapSettingsBoundaries.selectors';
import {
  type BoundaryFilter,
  type BoundaryFiltersState,
  type BoundaryLocationsFilter,
  type BoundaryLocationsFiltersState,
  type FilteredBoundaryItems,
} from './mapSettingsToolsStateBoundary.state';

export const emptyBoundaryTerritoryGroupFilter: BoundaryFilter = {
  filteredBoundaries: [],
  filteredBoundaryTerritories: [],
  showAll: TriStateRange.Full,
};

export const emptyBoundaryLocationsFilter: BoundaryLocationsFilter = {
  filteredBoundaries: [],
  filteredBoundaryTerritories: [],
};

export type BoundaryFiltersGeneric<T extends FilteredBoundaryItems> = {
  get: (boundaryGroupId: BoundaryGroupId) => T;
  entries: ReadonlyArray<{ boundaryGroupId: BoundaryGroupId; filter: T }>;
};

export type BoundaryFilters = BoundaryFiltersGeneric<BoundaryFilter>;
export type BoundaryLocationsFilters = Readonly<{
  all: BoundaryFiltersGeneric<BoundaryLocationsFilter>;
  isolated: BoundaryFiltersGeneric<BoundaryLocationsFilter>;
}>;

// Boundary Filters Selectors

export const boundaryFiltersSelector = (state: AppState): BoundaryFilters => {
  const baseFilters = boundaryFiltersStateSelector(state);
  const boundaryTerritoryGroups = boundaryToolActiveBTGsSelector(state);

  return getBoundaryTerritoryGroupFiltersMemoized(baseFilters, boundaryTerritoryGroups);
};

export const useBoundaryFilters = () =>
  useSelector(boundaryFiltersSelector);

// Boundary Locations Filters Selectors

const boundaryLocationsFiltersSelector = (state: AppState): BoundaryLocationsFilters =>
  getBoundaryTerritoryGroupLocationsFiltersMemoized(
    boundaryLocationsFiltersStateSelector(state),
    mapSettingsBoundariesIsolatedBoundariesSelector(state),
  );

export const useBoundaryLocationsFilters = () =>
  useSelector(boundaryLocationsFiltersSelector);

const getBoundaryTerritoryGroupFiltersMemoized = memoizeWeak((
  filters: BoundaryFiltersState,
  boundaryTerritoryGroups: readonly BoundaryTerritoryGroup[],
): BoundaryFilters => {
  // Create a mapping of boundary territory group IDs to boundary group IDs
  const btgIdToBoundaryGroupIdMap = new Map<number, BoundaryGroupId>();
  boundaryTerritoryGroups.forEach((btg: BoundaryTerritoryGroup) => {
    btgIdToBoundaryGroupIdMap.set(btg.boundaryTerritoryGroupId, btg.boundaryGroupId);
  });

  return {
    get: (boundaryGroupId: BoundaryGroupId) => {
      return filters[boundaryGroupId] ?? emptyBoundaryTerritoryGroupFilter;
    },
    entries: boundaryTerritoryGroups
      .map((boundaryTerritoryGroup) => {
        const boundaryGroupId = boundaryTerritoryGroup.boundaryGroupId;
        const filter = filters[boundaryGroupId];
        return filter ? {
          boundaryGroupId,
          filter,
        } : null;
      })
      .filter((entry) => notNullsy(entry)),
  };
});

export const getBoundaryTerritoryGroupLocationsFiltersMemoized = memoizeWeak((
  locationsFilter: BoundaryLocationsFiltersState,
  isolatedBoundaries: BoundaryLocationsFiltersState,
): BoundaryLocationsFilters => {
  const allBoundaryGroupIds = [...numberObjectKeys(locationsFilter), ...numberObjectKeys(isolatedBoundaries)];
  const combinedFilters = allBoundaryGroupIds.reduce((acc, boundaryGroupId) => ({
    ...acc,
    [boundaryGroupId]: {
      ...acc[boundaryGroupId],
      filteredBoundaries: [
        ...(locationsFilter[boundaryGroupId]?.filteredBoundaries ?? []),
        ...(isolatedBoundaries[boundaryGroupId]?.filteredBoundaries ?? []),
      ],
      filteredBoundaryTerritories: [
        ...(locationsFilter[boundaryGroupId]?.filteredBoundaryTerritories ?? []),
        ...(isolatedBoundaries[boundaryGroupId]?.filteredBoundaryTerritories ?? []),
      ],
    },
  }), {} as BoundaryLocationsFiltersState);

  const toEntries = (filters: BoundaryLocationsFiltersState) => Object.entries(filters)
    .filter((pair): pair is [string, BoundaryLocationsFilter] => {
      const [_, filter] = pair;
      return !!filter && (!!filter.filteredBoundaries.length || !!filter.filteredBoundaryTerritories.length);
    })
    .map(([key, filter]) => ({
      boundaryGroupId: parseInt(key, 10),
      filter: filter satisfies BoundaryLocationsFilter,
    }));

  return {
    all: {
      get: (boundaryGroupId: BoundaryGroupId) => combinedFilters[boundaryGroupId] ?? emptyBoundaryLocationsFilter,
      entries: toEntries(combinedFilters),
    },
    isolated: {
      get: (boundaryGroupId: BoundaryGroupId) => isolatedBoundaries[boundaryGroupId] ?? emptyBoundaryLocationsFilter,
      entries: toEntries(isolatedBoundaries),
    },
  };
});
